import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant';
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useTranslation } from 'react-i18next'
import { useTransactionRefresh } from '@/hooks'
export default React.memo(function SetPassword() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { refreshAfterTransaction } = useTransactionRefresh()
  const { state } = useLocation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{ password?: string, confirmPassword?: string }>({})
  const [loading, setLoading] = useState(false)
  const validatePassword = useCallback((pwd: string) => {
    if (pwd.length < 8) {
      return t('密码长度不能少于8个字符')
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(pwd)) {
      return t('密码必须包含大小写字母')
    }
    return ''
  }, [t])

  // 密码输入框
  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value)
    const error = validatePassword(value)
    setErrors(prev => ({ ...prev, password: error }))
  }, [validatePassword])
  // 确认密码输入框
  const handleConfirmPasswordChange = useCallback((value: string) => {
    setConfirmPassword(value)
    const error = value !== password ? t('两次输入的密码不一致') : ''
    setErrors(prev => ({ ...prev, confirmPassword: error }))
  }, [password, t])
  // 提交
  const handleSubmit = useCallback(async () => {
    const passwordError = validatePassword(password)
    const confirmError = confirmPassword !== password ? t('两次输入的密码不一致') : ''

    if (passwordError || confirmError) {
      setErrors({ password: passwordError, confirmPassword: confirmError })
      return
    }
    try {
      setLoading(true)
      const data = await APIWeb.register({
        account: state?.account || '',
        password,
        confirm_password: confirmPassword,
        code: state?.code || '',
      })

      // 存储 token 到 localStorage
      localStorage.setItem('token', data.token || '')
      await refreshAfterTransaction()
      Toast.success({
        message: t('注册成功'),
        onClose: () => navigate('/home')
      })
    } finally {
      setPassword('')
      setConfirmPassword('')
      setErrors({})
      setLoading(false)
    }
    // 密码设置成功，跳转到选择账户类型页面
    // navigate('/select-account-type')
  }, [password, confirmPassword, validatePassword, navigate, state, t])

  const isFormValid = password && confirmPassword && !errors.password && !errors.confirmPassword

  return (
    <PageLayout>
      <Header leftChildren={<Back />} centerChildren={<div className='text-3.75 font-medium text-black'>{t('设置密码')}</div>} />
      {/* Content */}
      <div className="px-6 py-6">
        <div className="space-y-6 max-w-md mx-auto">
          {/* Password Input */}
          <div className="space-y-2">
            <div className="text-xs text-[#1A1A1A] mb-1">{t('请输入您要设置的密码')}</div>
            <CustomTextInput
              isErrored={Boolean(errors.password)}
              value={password}
              onChangeText={handlePasswordChange}
              secureTextEntry={true}
              isShowSmallEyes={true}
              placeholder={t('长度不小于8字符并包含大小写')}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>
          <div className="space-y-2">
            <div className="text-xs text-[#1A1A1A] mb-1">{t('请再次输入您要设置的密码')}</div>
            <CustomTextInput
              isErrored={Boolean(errors.confirmPassword)}
              value={confirmPassword}
              onChangeText={handleConfirmPasswordChange}
              secureTextEntry={true}
              isShowSmallEyes={true}
              placeholder={t('确保和您第一次输入的密码一致')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <CustomButton
            onClick={handleSubmit}
            loading={loading}
            disabled={!isFormValid || loading}
          >
            {t('完成')}
          </CustomButton>

        </div>
      </div>
    </PageLayout>
  )
})